import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { SERVICES, IMAGES } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const CHAPTER_IMAGES = { "01": IMAGES.precisionTools, "02": IMAGES.patioWash, "03": IMAGES.coastalHome };

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <p className="font-mono-accent mb-4 text-xs uppercase tracking-[0.24em] text-amber-600">
          What I Handle
        </p>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
          Three chapters of care,{" "}
          <span className="font-serif-accent font-medium italic text-slate-500">
            one accountable craftsman.
          </span>
        </h2>
      </motion.div>

      <div className="space-y-20">
        {SERVICES.map((s, idx) => (
          <motion.article
            key={s.number}
            data-testid={`service-chapter-${s.number}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, ease: EASE }}
            className={`grid items-center gap-10 border-t border-stone-200 pt-12 lg:grid-cols-[auto_1fr_0.7fr] lg:gap-16 ${
              idx % 2 === 1 ? "lg:[&>*:first-child]:order-3 lg:[&>*:nth-child(2)]:order-2 lg:[&>*:last-child]:order-1" : ""
            }`}
          >
            <span
              aria-hidden
              className="font-mono-accent text-6xl font-light leading-none text-amber-600/40 sm:text-7xl"
            >
              {s.number}
            </span>
            <div>
              <h3 className="font-serif-accent text-xl font-semibold text-slate-900 sm:text-2xl">
                {s.title}
              </h3>
              <p className="mt-2 text-base text-slate-600">{s.tagline}</p>
              <ul className="mt-6 space-y-3">
                {s.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="group relative">
              <div
                aria-hidden
                className="absolute -inset-2.5 rounded-[1.8rem] border border-stone-300/60 transition-transform duration-500 group-hover:rotate-1"
              />
              <div className="relative overflow-hidden rounded-[1.6rem] shadow-lg shadow-slate-900/10">
                <img
                  src={CHAPTER_IMAGES[s.number]}
                  alt={s.title}
                  data-testid={`service-image-${s.number}`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
